/**
 * ============================================================================
 * DATA LAYER: GESTIÓN DE MENSAJES
 * ============================================================================
 * Encargado de realizar las peticiones HTTP al Backend (.NET API).
 * Maneja la construcción de URLs, configuración de headers y captura básica de errores de red.
 */

// URL Base del controlador de mensajes
const API_BASE = process.env.REACT_APP_API_URL + 'messages';

// /**
//  * Obtiene una página de mensajes desde la API.
//  * Soporta paginación por cursor y ordenamiento.
//  * * @param {Object} params - Parámetros de consulta
//  * @param {number} params.limit - Cantidad de mensajes a traer
//  * @param {number|null} params.cursor - ID del último mensaje visto (para paginación)
//  * @param {string} params.sort - Dirección del ordenamiento ('ASC' o 'DESC')
//  */
// const GetMessagesPage = async ({ limit = 30, cursor = null, sort = 'DESC', onlyWithPhone }) => {
//     try {
//         // Construcción de la URL con query params
//         let url = `${API_BASE}/list?limit=${limit}&sort=${sort}`;
//         if (cursor) url += `&cursor=${cursor}`;
//         if (onlyWithPhone) url += `&onlyWithPhone=true`;

//         const response = await fetch(url, {
//             method: 'GET',
//             headers: { 'Content-Type': 'application/json' }
//         });

//         if (!response.ok) {
//             const errorData = await response.json();
//             return { success: false, message: errorData.message || 'Error al obtener mensajes.' };
//         }

//         const data = await response.json();
//         return { success: true, data: data }; // Espera { items: [], nextCursor: ... }
//     } catch (error) {
//         return { success: false, message: 'No ha sido posible conectar con el servidor.' };
//     }
// };

/**
 * Obtiene una página de mensajes desde la API.
 */
const GetMessagesPage = async ({ limit = 30, cursor = null, sort = 'DESC', onlyWithPhone = false }) => {
    try {
        // Construcción de la URL
        let url = `${API_BASE}/list?limit=${limit}&sort=${sort}`;
        
        if (cursor) url += `&cursor=${cursor}`;
        if (onlyWithPhone) url += `&onlyWithPhone=true`;

        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return { success: false, message: errorData.message || 'Error al obtener mensajes.' };
        }

        const data = await response.json();
        return { success: true, data: data }; 
    } catch (error) {
        return { success: false, message: 'No ha sido posible conectar con el servidor.' };
    }
};

/**
 * Obtiene todos los mensajes desde la API sin límites de paginación (para exportación).
 */
const GetAllMessages = async ({ onlyWithPhone = false } = {}) => {
    try {
        let url = `${API_BASE}/all`;
        if (onlyWithPhone) url += `?onlyWithPhone=true`;

        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return { success: false, message: errorData.message || 'Error al obtener todos los mensajes.' };
        }

        const data = await response.json();
        return { success: true, data: data }; // Espera un array de mensajes
    } catch (error) {
        return { success: false, message: 'No ha sido posible conectar con el servidor.' };
    }
};

/**
 * Obtiene mensajes nuevos que hayan llegado después de un ID específico.
 * Útil para el botón "Cargar nuevos".
 * * @param {number} lastId - El ID más alto que tiene el cliente actualmente.
 */
const GetNewerMessages = async (lastId) => {
    try {
        const response = await fetch(`${API_BASE}/newer-than/${lastId}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return { success: false, message: errorData.message || 'Error al buscar nuevos mensajes.' };
        }

        const data = await response.json();
        return { success: true, data: data }; // Espera un array de mensajes
    } catch (error) {
        return { success: false, message: 'Error de conexión al buscar nuevos mensajes.' };
    }
};

/**
 * Envía un nuevo mensaje (Lado del Fan).
 */
const SendMessage = async (messageData) => {
    try {
        const response = await fetch(`${API_BASE}/send`, {
            method: 'POST',
            body: JSON.stringify({
                message: messageData.message,
                sender: messageData.sender
            }),
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return { success: false, message: errorData.message || 'Error al enviar mensaje.' };
        }

        return { success: true, data: null };
    } catch (error) {
        return { success: false, message: 'Error de conexión al enviar mensaje.' };
    }
};

/**
 * Alterna la visibilidad de un mensaje (Ocultar/Mostrar).
 */
const ToggleMessageVisibility = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/${id}/toggle-hide`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al cambiar visibilidad.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Alterna el estado de "Visto/No Visto" manualmente.
 */
const ToggleMessageShown = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/${id}/toggle-shown`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al cambiar estado visto.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Actualiza el diseño asignado a un mensaje específico.
 */
const SetMessageDesign = async (id, designId) => {
    try {
        const response = await fetch(`${API_BASE}/${id}/set-design?designId=${designId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al asignar diseño.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Elimina un mensaje permanentemente.
 */
const DeleteMessage = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/${id}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al eliminar.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Proyecta un mensaje en la pantalla gigante.
 */
const SelectMessage = async (id) => {
    try {
        const response = await fetch(`${API_BASE}/${id}/select`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al proyectar.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Quita el mensaje actual de la pantalla.
 */
const DeselectMessage = async () => {
    try {
        const response = await fetch(`${API_BASE}/clear`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al limpiar pantalla.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

/**
 * Reinicia la dinámica completa (Borra todo).
 */
const ResetMessages = async () => {
    try {
        const response = await fetch(`${API_BASE}/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) return { success: false, message: 'Error al reiniciar.' };
        return { success: true };
    } catch (error) {
        return { success: false, message: 'Error de conexión.' };
    }
};

const SetOverlayPosition = async (offset) => {
    try {
        //await fetch(`${API_BASE}/position?offset=${offset}`, { method: 'POST' });
        //return { success: true };

        const response = await fetch(`${API_BASE}/position?offset=${offset}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) return { success: false, message: 'Error al cambiar de posición' };
        return { success: true };
    } catch {
        return { success: false, message: '' };
    }
};

export { GetMessagesPage, GetAllMessages, GetNewerMessages, SendMessage, ToggleMessageVisibility, ToggleMessageShown, SetMessageDesign, DeleteMessage, SelectMessage, DeselectMessage, ResetMessages, SetOverlayPosition };









// const GetMessages = async (quantity = 10) => {
//     try {
//         const response = await fetch(process.env.REACT_APP_API_URL + `messages/list?limit=${quantity}`, {
//             method: 'GET',
//             headers: {
//                 'Content-Type': 'application/json',
//             }
//         })
//         debugger
//         if (!response.ok) {
//             // Manejar errores HTTP
//             const errorData = await response.json()
//             return { success: false, message: errorData.message || 'No ha sido posible obtener el listado de mensajes. Inténtalo nuevamente o inténtalo más tarde.' }
//         }
//         debugger
//         const data = await response.json()
//         debugger
//         // Retornar la respuesta exitosa
//         return { success: true, data: data }
//     } catch (error) {
//         return { success: false, message: 'No ha sido posible obtener el listado de mensajes. Inténtalo nuevamente o inténtalo más tarde' }
//     }
// }

// const SendMessage = async (message) => {
//     try {
//         const dataToSend = {
//             message: message.message,
//             sender: message.sender
//         }
//         debugger
//         const response = await fetch(process.env.REACT_APP_API_URL + 'messages/send', {
//             method: 'POST',
//             body: JSON.stringify(dataToSend),
//             headers: {
//                 'Content-Type': 'application/json'
//             }
//         })
//         debugger
//         if (!response.ok) {
//             // Manejar errores HTTP
//             const errorData = await response.json()
//             return { success: false, message: errorData.message || 'No ha sido posible enviar tu mensaje. Inténtalo nuevamente o inténtalo más tarde.' }
//         }
//         debugger
//         // Retornar la respuesta exitosa
//         return { success: true, data: null }
//     } catch (error) {
//         return { success: false, message: 'No ha sido posible enviar tu mensaje. Inténtalo nuevamente o inténtalo más tarde' }
//     }
// }

// export { GetMessages, SendMessage };